// ============================================================================
// ClientStore - Manages Client Data with Caching and Real-time Updates
// ============================================================================
// Events: 'client:loaded', 'client:fullDataLoaded', 'client:updated',
//         'client:deleted', 'client:created', 'client:error',
//         'client:invalidated', 'recent:updated'
//
// Notes are handled by NotesStore (state.notes).
// ============================================================================

import { supabase } from 'app/auth/auth.js';
import { ObservableStore } from './observable-store.js';

// The most matches searchClients returns. Exported so a picker can tell "these
// are all the matches" from "these are the first of many - keep typing".
export const CLIENT_SEARCH_LIMIT = 50;

/**
 * Store for managing client data
 * @class ClientStore
 * @extends ObservableStore
 */
class ClientStore extends ObservableStore {
    constructor() {
        super();
        this.recentClients = [];
        this.maxRecent = 5;
        this.pendingUpdates = new Map();
        this.updateVersions = new Map();
        this._unsubAll = null; // single all-clients realtime subscription
    }


    /**
     * Get a client by ID (from cache or fetch)
     * @param {string} clientId - The client ID
     * @param {number} [retries=3] - Number of retry attempts
     * @returns {Promise<Object|null>} The client data or null
     */
    async getClient(clientId, retries = 3) {
        // Check cache first
        const cached = this.cache.get(`client:${clientId}`);
        if (cached && cached.fullyLoaded) {
            this.trackRecent(clientId);
            return cached;
        }

        // Fetch from database with group and portfolio manager data
        try {
            const { data, error } = await supabase
                .from('clients')
                .select(`
                    *,
                    groups (
                        id,
                        group_name,
                        portfolio_manager,
                        profiles (
                            user_id,
                            full_name,
                            short_name
                        )
                    )
                `)
                .eq('id', clientId)
                .single();

            if (error) throw error;

            // Flatten portfolio manager data for easier access
            if (data && data.groups && data.groups.profiles) {
                data.portfolio_manager_name = data.groups.profiles.full_name || data.groups.profiles.short_name || 'Not assigned';
                data.portfolio_manager_id = data.groups.profiles.user_id;
            } else {
                data.portfolio_manager_name = 'Not assigned';
                data.portfolio_manager_id = null;
            }

            // Mark as partially loaded
            data.fullyLoaded = false;

            // Cache it
            this.cache.set(`client:${clientId}`, data);

            // Track as recent
            this.trackRecent(clientId);

            // Ensure single all-clients subscription is active
            this._ensureSubscription();

            // Emit event
            this.emit('client:loaded', data);

            // Start loading all related data in background
            this.loadClientFullData(clientId);

            return data;

        } catch (error) {
            // Retry logic
            if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return this.getClient(clientId, retries - 1);
            }

            // Invalidate cache on final failure
            this.cache.delete(`client:${clientId}`);

            this.emit('client:error', error);
            return null;
        }
    }

    /**
     * Load all client related data (background operation)
     * @param {string} clientId - The client ID
     * @private
     */
    async loadClientFullData(clientId) {
        try {
            // Load related data in parallel (notes handled by NotesStore)
            const [services, tasks, contacts] = await Promise.all([
                this.loadClientServices(clientId),
                this.loadClientTasks(clientId),
                this.loadClientContacts(clientId)
            ]);

            const clientData = this.cache.get(`client:${clientId}`);
            if (clientData) {
                clientData.services = services;
                clientData.tasks = tasks;
                clientData.contacts = contacts;
                clientData.fullyLoaded = true;

                this.cache.set(`client:${clientId}`, clientData, 10 * 60 * 1000);
                this.emit('client:fullDataLoaded', { clientId, data: clientData });
            }
        } catch (error) {
            console.error(`[ClientStore] Error loading full data for client ${clientId}:`, error);
        }
    }

    /**
     * Load services for a client
     * @param {string} clientId - The client ID
     * @returns {Promise<Object[]>} Array of client services
     * @private
     */
    async loadClientServices(clientId) {
        try {
            const { data, error } = await supabase
                .from('client_services')
                .select(`
                    *,
                    services!client_services_service_id_fkey (
                        id,
                        code,
                        name,
                        is_active,
                        is_recurring,
                        recurrence_months,
                        period_type
                    )
                `)
                .eq('client_id', clientId)
                .eq('is_enabled', true);

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('[ClientStore] Error loading services:', error);
            return [];
        }
    }

    /**
     * Load tasks for a client
     * @param {string} clientId - The client ID
     * @returns {Promise<Object[]>} Array of client tasks
     * @private
     */
    async loadClientTasks(clientId) {
        try {
            const { data, error } = await supabase
                .from('tasks')
                .select(`
                    *,
                    services:service_id (
                        id,
                        code,
                        name
                    )
                `)
                .eq('client_id', clientId)
                .order('due_date', { ascending: false })
                .limit(50);

            if (error) throw error;

            // Attach workflow state info from the dropdown cache (no network).
            // Lazy import to avoid the state.js -> clientStore.js -> state.js cycle.
            const { state } = await import('./state.js');
            const tasks = (data || []).map(t => {
                const stateInfo = t.current_state_id
                    ? state.dropdown?.getServiceStatusById(t.current_state_id)
                    : null;
                return {
                    ...t,
                    service_workflow_states: stateInfo ? {
                        id: stateInfo.id,
                        display_name: stateInfo.display_name,
                        color_hex: stateInfo.color_hex
                    } : null
                };
            });
            return tasks;
        } catch (error) {
            console.error('[ClientStore] Error loading tasks:', error);
            return [];
        }
    }

    /**
     * Load contacts for a client
     * @param {string} clientId - The client ID
     * @returns {Promise<Object[]>} Array of client contacts
     * @private
     */
    async loadClientContacts(clientId) {
        try {
            const { data, error } = await supabase
                .from('contacts')
                .select('*')
                .eq('client_id', clientId)
                .order('name');

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('[ClientStore] Error loading contacts:', error);
            return [];
        }
    }

    /**
     * Track a client as recently accessed
     * @param {string} clientId - The client ID
     * @private
     */
    trackRecent(clientId) {
        // Remove if already in list
        this.recentClients = this.recentClients.filter(id => id !== clientId);

        // Add to front
        this.recentClients.unshift(clientId);

        // Trim to max
        if (this.recentClients.length > this.maxRecent) {
            this.recentClients.pop();
        }

        this.emit('recent:updated', this.recentClients);
    }

    /**
     * Ensure the single all-clients realtime subscription is active.
     * Routes changes to cached clients instead of creating per-client channels.
     * @private
     */
    _ensureSubscription() {
        if (this._unsubAll) return;

        this._unsubAll = this.subscriptions.subscribe(
            'clients',
            null,
            (payload) => {
                const clientId = payload.new?.id ?? payload.old?.id;
                if (!clientId) return;

                const key = `client:${clientId}`;

                if (payload.eventType === 'UPDATE') {
                    const oldData = this.cache.get(key);
                    // Only process if this client is in our cache
                    if (!oldData) return;

                    const newData = payload.new;
                    const hasPendingUpdate = this.pendingUpdates.has(clientId);

                    // Find which fields changed
                    const changedFields = [];
                    if (!hasPendingUpdate) {
                        for (const field of Object.keys(newData)) {
                            if (field === 'updated_at' || field === 'created_at') continue;
                            const oldVal = oldData[field] ?? null;
                            const newVal = newData[field] ?? null;
                            if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
                                changedFields.push(field);
                            }
                        }
                    }

                    // Update cache
                    this.cache.set(key, newData);

                    this.emit('client:updated', {
                        client: newData,
                        changedFields,
                        isRemote: !hasPendingUpdate && changedFields.length > 0
                    });
                } else if (payload.eventType === 'DELETE') {
                    this.cache.delete(key);
                    this.recentClients = this.recentClients.filter(id => id !== clientId);
                    this.emit('client:deleted', clientId);
                }
            }
        );
    }

    /**
     * Update a client with optimistic updates and retry logic
     * @param {string} clientId - The client ID
     * @param {Object} updates - Fields to update
     * @param {number} [retries=3] - Number of retry attempts
     * @returns {Promise<{data: Object|null, error: Error|null}>}
     */
    async updateClient(clientId, updates, retries = 3) {
        const cacheKey = `client:${clientId}`;
        const previousData = this.cache.get(cacheKey);

        // Generate update version for conflict detection
        const updateVersion = Date.now();
        this.updateVersions.set(clientId, updateVersion);

        // Optimistic update - update cache immediately
        if (previousData) {
            const optimisticData = { ...previousData, ...updates };
            this.cache.set(cacheKey, optimisticData);
            // Local update - no animation needed
            this.emit('client:updated', {
                client: optimisticData,
                changedFields: Object.keys(updates),
                isRemote: false
            });
        }

        // Track pending update
        this.pendingUpdates.set(clientId, updates);

        try {
            const { data, error } = await supabase
                .from('clients')
                .update(updates)
                .eq('id', clientId)
                .select()
                .single();

            if (error) throw error;

            // Check if this update is still the latest
            if (this.updateVersions.get(clientId) === updateVersion) {
                // Update cache with server response
                this.cache.set(cacheKey, data);
                // Local update confirmed - no animation
                this.emit('client:updated', {
                    client: data,
                    changedFields: Object.keys(updates),
                    isRemote: false
                });
            }

            // Clear pending update
            this.pendingUpdates.delete(clientId);

            return { data, error: null };

        } catch (error) {
            // Retry logic
            if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return this.updateClient(clientId, updates, retries - 1);
            }

            // Rollback optimistic update on final failure
            if (previousData) {
                this.cache.set(cacheKey, previousData);
                this.emit('client:updated', {
                    client: previousData,
                    changedFields: Object.keys(updates),
                    isRemote: false,
                    isRollback: true
                });
            }

            // Clear pending update
            this.pendingUpdates.delete(clientId);

            this.emit('client:error', error);
            return { data: null, error };
        }
    }

    /**
     * Get all clients with optional limit
     * @param {number} [limit=20] - Maximum clients to return
     * @returns {Promise<Object[]>} Array of clients
     */
    async getAllClients(limit = 20) {
        try {
            const { data, error } = await supabase
                .from('clients')
                .select('*')
                .order('client_name')
                .limit(limit);

            if (error) throw error;

            return data || [];

        } catch (error) {
            return [];
        }
    }

    /**
     * Search clients by name, codes, and tax numbers
     * @param {string} query - Search query
     * @returns {Promise<Object[]>} Array of matching clients
     */
    async searchClients(query) {
        const trimmed = (query || '').trim();
        if (!trimmed) {
            return [];
        }

        // Commas AND spaces become a % wildcard, so "Brussow S" finds
        // "Brussow, S" as well as the other way round - half the client names
        // are stored "Surname, Initials" and nobody types the comma. Brackets,
        // quotes and backslashes go the same way: PostgREST's or-tree would
        // otherwise read them as syntax and the search would match nothing.
        const pattern = `%${trimmed.replace(/[,\s()"\\]+/g, '%')}%`;

        try {
            const { data, error } = await supabase
                .from('clients')
                .select('id, client_name, legacy_code, billing_code, tax_number')
                .or([
                    `client_name.ilike.${pattern}`,
                    `legacy_code.ilike.${pattern}`,
                    `billing_code.ilike.${pattern}`,
                    `tax_number.ilike.${pattern}`,
                    `vat_number.ilike.${pattern}`,
                    `ck_national_id_number.ilike.${pattern}`,
                    `paye_number.ilike.${pattern}`
                ].join(','))
                .order('client_name')
                .limit(CLIENT_SEARCH_LIMIT);

            if (error) throw error;

            return data || [];

        } catch (error) {
            console.error('[ClientStore] Error searching clients:', error);
            return [];
        }
    }

    /**
     * Get recently accessed clients with full data
     * @returns {Promise<Object[]>} Array of recent clients
     */
    async getRecentClients() {
        const results = await Promise.all(
            this.recentClients.map(id => this.getClient(id))
        );
        return results.filter(Boolean);
    }

    /**
     * Invalidate client cache
     * @param {string} clientId - The client ID
     */
    invalidateClient(clientId) {
        this.cache.delete(`client:${clientId}`);
        this.emit('client:invalidated', clientId);
    }

    /**
     * Check if client has pending updates
     * @param {string} clientId - The client ID
     * @returns {boolean}
     */
    hasPendingUpdates(clientId) {
        return this.pendingUpdates.has(clientId);
    }

    /**
     * Get pending updates for a client
     * @param {string} clientId - The client ID
     * @returns {Object|undefined}
     */
    getPendingUpdates(clientId) {
        return this.pendingUpdates.get(clientId);
    }

    /**
     * Clean up all resources
     */
    destroy() {
        if (this._unsubAll) {
            this._unsubAll();
            this._unsubAll = null;
        }
        super.destroy();
    }

    /**
     * Create a new client
     * @param {Object} clientData - Client creation data
     * @param {string} clientData.client_name - Client name
     * @param {string} [clientData.group_id] - Optional group ID
     * @param {number} [clientData.year_end_month=12] - Year end month
     * @param {number} [clientData.company_type_id=1] - Company type ID
     * @returns {Promise<Object>} Created client data
     */
    async createClient({ client_name, group_id = null, year_end_month = 12, company_type_id = 1 }) {
        try {
            const payload = {
                client_name,
                group_id,
                year_end_month,
                company_type_id
            };

            const { data, error } = await supabase
                .from('clients')
                .insert(payload)
                .select('*')
                .single();

            if (error) throw error;

            if (data?.id) {
                this.cache.set(`client:${data.id}`, data);
                this._ensureSubscription();
                this.trackRecent(data.id);
                this.emit('client:created', data);
            }

            return data;
        } catch (error) {
            console.error('[ClientsStore] Failed to create client:', error);
            this.emit('client:error', error);

            // Enhance error with status code for better handling upstream
            if (error.code === '23505') {
                error.isDuplicate = true;
            }

            throw error;
        }
    }
}

export { ClientStore };
